import { useContext, useCallback } from 'react';
import { TransferQueueContext } from '@contexts/TransferQueueContext';
import { uploadFile } from '@services/api/r2ApiClient';
import type { StorageScope } from '@services/api/apiConfig';
import type { TransferTask } from '@/types/transfer.types';

export function useUploadQueue() {
  const ctx = useContext(TransferQueueContext);
  if (!ctx) throw new Error('useUploadQueue must be used within TransferQueueProvider');

  const enqueue = useCallback(
    async (file: File, targetPath: string, storageScope: StorageScope): Promise<{
      success: boolean;
      error?: string;
      publicationStatus?: 'delivered' | 'pending' | 'recorded' | 'failed';
    }> => {
      const id = `${Date.now()}-${file.name}`;
      const task: TransferTask = {
        id,
        file,
        targetPath,
        orgId: storageScope.orgId,
        connectionId: storageScope.connectionId,
        status: 'pending',
        progress: 0,
        startedAt: Date.now(),
      };

      ctx.addTask(task);
      ctx.updateTask(id, { status: 'uploading' });

      try {
        const uploaded = await uploadFile(targetPath, file, (pct) => {
          ctx.updateTask(id, { progress: pct });
        }, storageScope);
        ctx.updateTask(id, { status: 'completed', progress: 100, completedAt: Date.now() });
        return { success: true, publicationStatus: uploaded.publication?.status };
      } catch (err: any) {
        const message = err?.message || 'Upload failed';
        ctx.updateTask(id, { status: 'failed', error: message });
        return { success: false, error: message };
      }
    },
    [ctx]
  );

  return {
    tasks: ctx.tasks,
    enqueue,
    clearCompleted: ctx.clearCompleted,
  };
}
